import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import { Calendar, Plus, Trash2, AlertCircle, CheckCircle } from 'lucide-react';
import api from '@/lib/api';
import { ReminderDueDate } from '@/types/schedule';

interface ReminderDueDatesManagerProps {
  clientId: number;
  clientName?: string;
}

const ReminderDueDatesManager: React.FC<ReminderDueDatesManagerProps> = ({ clientId, clientName }) => {
  const [dueDates, setDueDates] = useState<ReminderDueDate[]>([]);
  const [newDueDay, setNewDueDay] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Buscar datas de vencimento ao montar o componente
  useEffect(() => {
    fetchDueDates();
  }, [clientId]);

  const fetchDueDates = async () => {
    try {
      setLoading(true);
      setError(null); // Limpar erros anteriores ao buscar
      console.log(`📅 [ReminderDueDatesManager] Buscando datas para cliente ${clientId}...`);
      
      const response = await api.get<{ success: boolean; data: ReminderDueDate[] }>(
        `/reminder-due-dates/${clientId}`
      );
      
      console.log(`📅 [ReminderDueDatesManager] Resposta completa:`, response);
      
      // api.get retorna os dados diretamente, não response.data
      if (response?.success) {
        const dates = response.data || [];
        setDueDates(dates);
        console.log(`✅ [ReminderDueDatesManager] Datas carregadas: ${dates.length} datas`, dates);
      } else {
        console.warn('⚠️ [ReminderDueDatesManager] Resposta sem success:', response);
        setDueDates([]);
      }
    } catch (err: any) {
      console.error('❌ [ReminderDueDatesManager] Erro ao buscar datas de vencimento:', err);
      console.error('❌ [ReminderDueDatesManager] Detalhes:', {
        status: err.response?.status,
        data: err.response?.data,
        message: err.message
      });
      // Não mostrar erro na busca inicial para não confundir o usuário
      // setError('Erro ao carregar datas de vencimento');
      setDueDates([]); // Garantir que o estado está vazio em caso de erro
    } finally {
      setLoading(false);
    }
  };

  const handleAddDueDate = async () => {
    // Validar input
    const day = parseInt(newDueDay);
    if (isNaN(day) || day < 1 || day > 31) {
      setError('Digite um dia válido entre 1 e 31');
      return;
    }

    // Verificar se já existe localmente (mas não bloquear - pode estar desincronizado)
    const existsLocally = dueDates.some(d => d.due_day === day);
    if (existsLocally) {
      setError(`O dia ${day} já está cadastrado`);
      // Recarregar lista para garantir sincronização
      await fetchDueDates();
      return;
    }

    try {
      setLoading(true);
      setError(null);
      setSuccess(null);

      console.log(`📅 [ReminderDueDatesManager] Tentando adicionar dia ${day} para cliente ${clientId}`);
      
      const response = await api.post<{ success: boolean; message: string; data: ReminderDueDate }>(
        `/reminder-due-dates/${clientId}`,
        { due_day: day }
      );

      console.log(`📅 [ReminderDueDatesManager] Resposta completa da API:`, response);

      // api.post retorna os dados diretamente, não response.data
      if (response?.success) {
        setSuccess(`Dia ${day} adicionado com sucesso!`);
        setNewDueDay('');
        
        // Aguardar um pouco antes de buscar para garantir que o backend processou
        await new Promise(resolve => setTimeout(resolve, 100));
        
        // Sempre buscar novamente após criar, mesmo que tenha dado sucesso
        // Isso garante que estamos sincronizados com o backend
        await fetchDueDates();
        
        // Limpar mensagem de sucesso após 3s
        setTimeout(() => setSuccess(null), 3000);
      } else {
        const errorMsg = response?.message || 'Erro ao adicionar data';
        setError(errorMsg);
        console.warn('⚠️ [ReminderDueDatesManager] Resposta sem success:', response);
        // Se deu erro mas pode ter criado, buscar novamente
        await fetchDueDates();
      }
    } catch (err: any) {
      console.error('❌ [ReminderDueDatesManager] Erro ao adicionar data de vencimento:', err);
      console.error('❌ [ReminderDueDatesManager] Detalhes do erro:', {
        status: err.response?.status,
        data: err.response?.data,
        message: err.message
      });
      
      const errorMessage = err.response?.data?.error || err.response?.data?.message || err.message || 'Erro ao adicionar data';
      
      // Se o erro for que já existe, buscar novamente para sincronizar
      if (errorMessage.includes('já está cadastrada') || errorMessage.includes('já existe')) {
        setError(`O dia ${day} já está cadastrado. Atualizando lista...`);
        // Buscar novamente para sincronizar
        await fetchDueDates();
        // Limpar erro após 2s
        setTimeout(() => setError(null), 2000);
      } else {
        setError(errorMessage);
      }
    } finally {
      setLoading(false);
    }
  };


  const handleDelete = async (dueDate: ReminderDueDate) => {
    if (!window.confirm(`Tem certeza que deseja remover o dia ${dueDate.due_day}?`)) {
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const response = await api.delete<{ success: boolean; message: string }>(
        `/reminder-due-dates/${dueDate.id}`
      );

      // api.delete retorna os dados diretamente, não response.data
      if (response?.success) {
        setSuccess(`Dia ${dueDate.due_day} removido com sucesso!`);
        await fetchDueDates();
        
        // Limpar mensagem de sucesso após 3s
        setTimeout(() => setSuccess(null), 3000);
      } else {
        setError('Erro ao remover data');
      }
    } catch (err) {
      console.error('Erro ao remover data de vencimento:', err);
      setError('Erro ao remover data');
    } finally {
      setLoading(false);
    }
  };

  // Ordenar datas por dia
  const sortedDueDates = [...dueDates].sort((a, b) => a.due_day - b.due_day);

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2">
            <Calendar className="h-5 w-5" />
            Datas de Vencimento
            {sortedDueDates.length > 0 && (
              <Badge variant="secondary" className="ml-2">
                {sortedDueDates.length} {sortedDueDates.length === 1 ? 'data' : 'datas'}
              </Badge>
            )}
          </CardTitle>
        </div>
        <p className="text-sm text-gray-600 mt-2">
          Configure os dias do mês em que este cliente tem boletos vencendo.
          Os lembretes serão enviados automaticamente baseados nesses dias.
        </p>
      </CardHeader>

      <CardContent>
        {/* Mensagens de erro/sucesso */}
        {error && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md flex items-center gap-2 text-red-700">
            <AlertCircle className="h-4 w-4" />
            <span className="text-sm">{error}</span>
          </div>
        )}
        
        {success && (
          <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-md flex items-center gap-2 text-green-700">
            <CheckCircle className="h-4 w-4" />
            <span className="text-sm">{success}</span>
          </div>
        )}

        {/* Adicionar nova data */}
        <div className="flex gap-2 mb-6">
          <Input
            type="number"
            min="1"
            max="31"
            placeholder="Dia (1-31)"
            value={newDueDay}
            onChange={(e) => {
              setNewDueDay(e.target.value);
              setError(null);
            }}
            onKeyPress={(e) => {
              if (e.key === 'Enter') {
                handleAddDueDate();
              }
            }}
            className="w-32"
            disabled={loading}
          />
          <Button
            onClick={handleAddDueDate}
            disabled={loading || !newDueDay}
            className="flex items-center gap-2 bg-green-600 hover:bg-green-700"
          >
            <Plus className="h-4 w-4" />
            Adicionar Dia
          </Button>
        </div>

        {/* Lista de datas - estilo similar aos lembretes */}
        {loading && sortedDueDates.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            <Calendar className="h-12 w-12 mx-auto mb-3 opacity-50 animate-pulse" />
            <p className="text-sm">Carregando datas de vencimento...</p>
          </div>
        ) : sortedDueDates.length === 0 ? (
          <div className="text-center text-gray-500 py-8">
            <Calendar className="h-12 w-12 mx-auto mb-3 opacity-50" />
            <p>Nenhuma data de vencimento configurada</p>
            <p className="text-xs">Adicione dias de vencimento para ativar os lembretes</p>
          </div>
        ) : (
          <div className="space-y-3">
            {sortedDueDates.map((dueDate) => (
              <div
                key={dueDate.id}
                className="flex items-center justify-between p-4 border-2 rounded-lg hover:border-gray-200 transition-colors border-gray-100"
              >
                <div className="flex items-center gap-4">
                  <div className="w-10 h-10 rounded-full bg-orange-100 text-orange-600 flex items-center justify-center">
                    <Calendar className="h-5 w-5" />
                  </div>
                  
                  <div>
                    <div className="flex items-center gap-2 flex-wrap">
                      <Badge className="font-mono bg-orange-100 text-orange-800 border-orange-300">
                        DIA {dueDate.due_day}
                      </Badge>
                    </div>
                    <p className="text-sm text-gray-600 mt-1">
                      Data de vencimento do mês • Dia {dueDate.due_day} de cada mês
                    </p>
                  </div>
                </div>

                <div className="flex items-center gap-3">
                  <Button
                    size="sm"
                    variant="destructive"
                    onClick={() => handleDelete(dueDate)}
                    disabled={loading}
                    className="hover:bg-red-600"
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Informação adicional */}
        {sortedDueDates.length > 0 && (
          <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-md">
            <p className="text-xs text-blue-800">
              <strong>💡 Dica:</strong> Após configurar as datas de vencimento, configure os lembretes
              na seção "Lembretes" acima (quantos dias antes e horário de envio).
            </p>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default ReminderDueDatesManager;

